<template>
  <div
    class="d-flex flex-wrap schedule-board-filters mb-xs-2 mb-md-2 gap-3 !items-end"
  >
    <Select
      v-model="department_ids"
      data-cy="departmentSelector"
      multiple
      :options="departmentOptions"
      label="Department"
      placeholder="All Departments "
      class="department-selector"
    >
      <template #item-row="{ option }">
        <div class="option-row">
          <span :data-cy="`departmentName-${option.label}`">{{
            option.label
          }}</span>
        </div>
      </template>
    </Select>
    <div class="d-flex">
      <Datepicker
        v-model="start"
        placeholder="Date"
        class="schedule-date-picker"
        data-cy="schedule-board-datepicker"
        buttons
        :step="isWeekView ? 7 : 1"
      />
      <div
        class="today-btn-cont"
        data-cy="today-btn"
        @click="handleDateChange(null)"
      >
        Today
      </div>
    </div>
    <!-- Desktop bar only; mobile surfaces the toggle in the page header -->
    <ScheduleViewToggle class="hidden md:inline-flex" />
    <CalendarEventModal
      v-if="eventModalOpen"
      :calendar-event="calendarEvent"
      @close="closeEventModal"
    ></CalendarEventModal>
    <CheckAvailabilityModal
      class="availability-btn hidden md:block"
    ></CheckAvailabilityModal>
    <CreateEventButton
      data-cy="new-event"
      :default-date="scheduleBoardFilters.start"
    />
  </div>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { reportRumError, trackRumAction } from "@/lib/datadog.js";
import { EventBus } from "@/lib/event_bus.js";
import { syncStartParam } from "@/lib/scheduleStartParam";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Datepicker from "@/components/Datepicker.vue";
import CalendarEventModal from "@/Scheduler/CalendarEventModal.vue";
import CheckAvailabilityModal from "@/Scheduler/CheckAvailabilityModal.vue";
import ScheduleViewToggle from "@/Scheduler/ScheduleViewToggle.vue";
import CreateEventButton from "@/SchedulerV2/CreateEventButton.vue";
import { CalendarEvent } from "@/typings/calendarEvent";

type BoardView = "day" | "week";

interface DepartmentRecord {
  id: number;
  name: string;
  archived: boolean;
}

const store = useStore();

const calendarEvent = ref(new CalendarEvent());
const eventModalOpen = ref(false);

const scheduleBoardFilters = computed(() => store.state.scheduleBoardFilters);
const departmentOptions = computed(() =>
  (store.state.departments as DepartmentRecord[])
    .filter((dept) => !dept.archived)
    .map((dept) => ({ label: dept.name, value: dept.id }))
);
const isWeekView = computed<boolean>(
  () => store.getters.scheduleWeekViewActive
);

const department_ids = computed({
  get: () => scheduleBoardFilters.value.department_ids,
  set(val: string[] | null) {
    const departmentIds = val ? val.join(",").trim() : null;
    updateQueryParam("department_ids", departmentIds, true);
    setScheduleBoardFilters({ department_ids: val });
  }
});

const start = computed({
  get: () => scheduleBoardFilters.value.start,
  set: (val: string | Date | null) => handleDateChange(val)
});

function setScheduleBoardFilters(filters: Record<string, unknown>) {
  return store.dispatch("setScheduleBoardFilters", filters);
}

function handleDateChange(val: string | Date | null) {
  const date = val || new Date();
  updateDateText(date);
  setScheduleBoardFilters({ start: date });
}

// Read the calendar's current date defensively — used only to observe whether
// a gotoDate actually advanced the view.
function currentCalendarDate(): string | null {
  try {
    const fc = window._FC;
    return fc?.getDate ? fc.getDate().toISOString() : null;
  } catch {
    return null;
  }
}

function updateDateText(date: string | Date) {
  const newDate = moment(date);
  syncStartParam(newDate, { replace: true });
  // The header label is SchedulePage's reactive title — it updates from
  // the store; writing it here would be overwritten on the next render.
  // setTimeout defers the calendar nav to its own call stack so the date
  // change paints before FullCalendar re-renders.
  setTimeout(() => {
    // Observability (ENG-241): day-nav failures ("schedule doesn't change until
    // I refresh") aren't reproducible on demand, so record on every nav whether
    // the global calendar handle exists, whether gotoDate actually advanced the
    // view, and capture any throw (the prime suspect). Additive only — the
    // rethrow preserves the prior (unhandled) behavior.
    const fcPresent = Boolean(window._FC);
    const dateBefore = currentCalendarDate();
    const requested = newDate.toISOString();
    try {
      if (window._FC) {
        window._FC.gotoDate(date);
      } else {
        EventBus.$emit("schedule-board-date-change", newDate);
      }
      trackRumAction("schedule_day_nav", {
        source: "filters",
        requested,
        fc_present: fcPresent,
        used_event_bus_fallback: !fcPresent,
        date_before: dateBefore,
        date_after: currentCalendarDate(),
        moved: fcPresent && dateBefore !== currentCalendarDate()
      });
    } catch (error) {
      reportRumError("schedule_day_nav_gotodate_threw", {
        source: "filters",
        requested,
        fc_present: fcPresent,
        date_before: dateBefore,
        thrown: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  });
}

function sanitizeView(view: unknown): BoardView | null {
  return view === "day" || view === "week" ? view : null;
}

// Accepts either the comma-joined string a query param carries or the array a
// cached filter holds, so a shared board URL works on a browser with no cache.
function parseDepartmentIds(value: unknown): string[] | null {
  const raw = Array.isArray(value) ? value.join(",") : value;
  if (typeof raw !== "string" && typeof raw !== "number") return null;

  const ids = String(raw)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  return ids.length ? ids : null;
}

function setDefaultFilters() {
  const {
    start: startParam,
    department_ids: deptParam,
    view
  } = getQueryVariables();
  // Pull cached filters
  const cachedRaw = localStorage.getItem("scheduleBoardFilters");
  const cachedFilters = cachedRaw ? JSON.parse(cachedRaw) : {};
  // Prefer query params over cached filters
  const departmentsFilter =
    parseDepartmentIds(deptParam) ??
    parseDepartmentIds(cachedFilters.department_ids);
  setScheduleBoardFilters({
    department_ids: departmentsFilter,
    // The board date is URL-driven: no start param means today
    start: startParam || new Date(),
    view: sanitizeView(view) || sanitizeView(cachedFilters.view) || "day"
  });
}

function editCalendarEvent(event: unknown) {
  calendarEvent.value = new CalendarEvent(event);
  eventModalOpen.value = true;
}

function closeEventModal() {
  eventModalOpen.value = false;
}

setDefaultFilters();

onMounted(() => {
  EventBus.$on("edit-calendar-event", editCalendarEvent);
});

onBeforeUnmount(() => {
  EventBus.$off("edit-calendar-event", editCalendarEvent);
});
</script>

<style lang="scss" scoped>
.department-selector {
  width: 250px;
}
.schedule-date-picker {
  width: 180px !important;
  margin-bottom: 0 !important;
}
.today-btn-cont {
  border: 1.5px solid var(--sera-color-fields-border, #9ca4aa);
  border-left: none;
  border-radius: 0 8px 8px 0;
  color: var(--text-links);
  font-weight: 500;
  height: 36px;
  align-self: end;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  top: 1px;
  position: relative;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
  &:hover {
    color: #fff;
    background-color: var(--primary-color);
  }
}
:deep(.event-button) {
  padding: 0.625rem 1.25rem !important;
  font-weight: 400 !important;
  background-color: var(--sera-color-text-links) !important;
  border: 1.5px solid var(--sera-color-fields-border, #9ca4aa);
  &:hover {
    background-color: rgb(30, 66, 159) !important; /* bg-blue-800 */
    border-color: rgb(30, 66, 159) !important; /* bg-blue-800 */
  }
}

:deep([data-cy="next-day-btn"]) {
  border-radius: 0 !important;
}
:deep(.option) {
  white-space: nowrap;
}

:deep(.select-box) {
  box-shadow: none !important;
}

:deep(.arrow-cont) {
  box-shadow: none !important;
}

:deep(.mx-input-wrapper .form-control) {
  box-shadow: none !important;
  height: 36px !important;
}

@media (prefers-reduced-motion: reduce) {
  .today-btn-cont {
    transition: none;
  }
}
</style>
